import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import Logo from '../components/Logo';

const AuthActionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // HashRouterではクエリパラメータが#の前に付く場合がある
  // 例: https://shiftmate.jp/?oobCode=xxx#/auth/action
  // useSearchParams()は#後のパラメータしか読めないため、window.location.searchも確認する
  const windowParams = new URLSearchParams(window.location.search);
  const mode = searchParams.get('mode') || windowParams.get('mode');
  const oobCode = searchParams.get('oobCode') || windowParams.get('oobCode');

  const [status, setStatus] = useState<'loading' | 'success' | 'redirecting' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleAction = async () => {
      if (!oobCode) {
        setStatus('error');
        setErrorMessage('無効なリンクです。認証コードが見つかりませんでした。');
        return;
      }

      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          if (auth.currentUser) {
              await auth.currentUser.reload();
          }
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage('未対応のアクション（' + mode + '）です。');
        }
      } catch (error: unknown) {
        console.error(error);
        setStatus('error');
        const firebaseError = error as { code?: string };
        if (firebaseError.code === 'auth/expired-action-code') {
            setErrorMessage('リンクの有効期限が切れています。再度認証メールを送信してください。');
        } else if (firebaseError.code === 'auth/invalid-action-code') {
            setErrorMessage('このリンクは既に使用されているか、無効になっています。');
        } else {
            setErrorMessage('認証処理中にエラーが発生しました。');
        }
      }
    };

    handleAction();
  }, [mode, oobCode]);

  if (status === 'redirecting') {
      return <Navigate to="/admin/setup" replace />;
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
        <div className="flex justify-center mb-12 lg:hidden">
          <Logo size="lg" />
        </div>
        <div className="bg-white text-left">
            {status === 'loading' && (
              <div className="py-12 flex flex-col items-center">
                <Loader2 className="animate-spin text-blue-600 mb-6" size={48} />
                <h2 className="text-xl font-bold text-slate-800 mb-2">認証情報を確認中</h2>
                <p className="text-slate-500 text-sm">少々お待ちください...</p>
              </div>
            )}
            {status === 'success' && (
              <div className="animate-in">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-sm border border-blue-100">
                        <CheckCircle2 size={28} strokeWidth={2.5} />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 tracking-tight">認証が完了しました</h1>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 mb-10 border border-slate-100">
                    <p className="text-slate-600 text-sm leading-relaxed font-bold mb-4">ご本人確認が正常に完了しました。</p>
                    <p className="text-slate-500 text-xs leading-relaxed">続けて、店舗やスタッフの登録を行う<span className="text-blue-600 font-bold">初期設定</span>を始めましょう。</p>
                </div>
                <button onClick={() => setStatus('redirecting')} className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-black transition-all active:scale-95 flex items-center justify-center group">
                  <span className="text-lg">初期設定をはじめる</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </button>
              </div>
            )}
            {status === 'error' && (
              <div className="animate-in">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center border border-red-100">
                        <AlertCircle size={28} />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 tracking-tight">認証エラー</h1>
                </div>
                <div className="bg-red-50 rounded-xl p-6 mb-8 border border-red-100">
                    <p className="text-red-600 text-sm font-bold leading-relaxed">{errorMessage}</p>
                </div>
                <button onClick={() => navigate('/login')} className="w-full bg-white border-2 border-slate-200 text-slate-800 font-bold py-4 rounded-xl hover:bg-slate-50 transition-all flex items-center justify-center">ログイン画面へ戻る</button>
              </div>
            )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default AuthActionPage;